import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, RefreshCw, Trash2, XCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const AUTH_KEY = "mocktpAdminAuth";
const UNDO_WARNING = "This action cannot be undone.";

const formatDateTime = (value) => {
  if (!value) return "Not recorded";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not recorded";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const confirmAction = (message) => window.confirm(`${message}\n\n${UNDO_WARNING}`);

export default function AdminTools() {
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [authToken, setAuthToken] = useState(() => sessionStorage.getItem(AUTH_KEY) || "");
  const [applications, setApplications] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState("");

  const authHeaders = useMemo(
    () => (authToken ? { Authorization: `Basic ${authToken}` } : {}),
    [authToken]
  );

  const handleAuthError = (err, fallback) => {
    if (err?.response?.status === 401) {
      sessionStorage.removeItem(AUTH_KEY);
      setAuthToken("");
      toast.error("Admin login required.");
      return;
    }
    if (err?.response?.status === 503) {
      toast.error("Admin access is not configured on the backend.");
      return;
    }
    toast.error(fallback);
  };

  const loadData = async () => {
    if (!authToken) return;
    setLoading(true);
    try {
      const [betaResponse, contactResponse] = await Promise.all([
        axios.get(`${API}/beta-signup`, { headers: authHeaders }),
        axios.get(`${API}/contact`, { headers: authHeaders }),
      ]);
      setApplications(
        [...betaResponse.data].sort((a, b) => {
          const aTime = new Date(a.created_at || a.createdAt || 0).getTime() || 0;
          const bTime = new Date(b.created_at || b.createdAt || 0).getTime() || 0;
          return bTime - aTime;
        })
      );
      setMessages(
        [...contactResponse.data].sort((a, b) => {
          const aTime = new Date(a.created_at || 0).getTime() || 0;
          const bTime = new Date(b.created_at || 0).getTime() || 0;
          return bTime - aTime;
        })
      );
    } catch (err) {
      handleAuthError(err, "Unable to load admin data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  const login = (event) => {
    event.preventDefault();
    if (!credentials.username.trim() || !credentials.password) {
      toast.error("Enter the admin username and password.");
      return;
    }
    const token = btoa(`${credentials.username.trim()}:${credentials.password}`);
    sessionStorage.setItem(AUTH_KEY, token);
    setAuthToken(token);
    setCredentials({ username: "", password: "" });
  };

  const logout = () => {
    sessionStorage.removeItem(AUTH_KEY);
    setAuthToken("");
    setApplications([]);
    setMessages([]);
  };

  const runDelete = async (actionKey, request, successMessage, refresh = loadData) => {
    setBusyAction(actionKey);
    try {
      await request();
      toast.success(successMessage);
      await refresh();
    } catch (err) {
      handleAuthError(err, "Delete action failed.");
    } finally {
      setBusyAction("");
    }
  };

  const deleteApplication = (application) => {
    if (!confirmAction(`Delete beta application for ${application.name || application.email || "this applicant"}?`)) return;
    runDelete(
      `beta-${application.id}`,
      () => axios.delete(`${API}/beta-signup/${application.id}`, { headers: authHeaders }),
      "Beta application deleted."
    );
  };

  const deleteContactMessage = (message) => {
    if (!confirmAction(`Delete contact message from ${message.name || message.email || "this contact"}?`)) return;
    runDelete(
      `contact-${message.id}`,
      () => axios.delete(`${API}/contact/${message.id}`, { headers: authHeaders }),
      "Contact message deleted."
    );
  };

  const clearApplications = () => {
    if (!confirmAction("Clear all beta applications?")) return;
    runDelete(
      "clear-beta",
      () => axios.delete(`${API}/beta-signup`, { headers: authHeaders }),
      "All beta applications cleared."
    );
  };

  const clearContactMessages = () => {
    if (!confirmAction("Clear all contact messages?")) return;
    runDelete(
      "clear-contact",
      () => axios.delete(`${API}/contact`, { headers: authHeaders }),
      "All contact messages cleared."
    );
  };

  if (!authToken) {
    return (
      <main className="min-h-screen bg-[#080C16] text-white flex items-center justify-center px-6">
        <form
          onSubmit={login}
          className="w-full max-w-sm rounded-2xl border border-white/10 bg-white/[0.03] p-6 shadow-2xl"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="h-11 w-11 rounded-xl bg-[#00DC82] text-[#080C16] flex items-center justify-center">
              <Lock size={22} />
            </div>
            <div>
              <h1 className="text-xl font-semibold tracking-tight">Admin Tools</h1>
              <p className="text-sm text-slate-400">MockTP internal dashboard</p>
            </div>
          </div>
          <label className="block text-sm text-slate-300 mb-2" htmlFor="admin-username">
            Username
          </label>
          <input
            id="admin-username"
            value={credentials.username}
            onChange={(event) => setCredentials((current) => ({ ...current, username: event.target.value }))}
            className="mb-4 h-11 w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 text-sm outline-none focus:border-[#00DC82]"
          />
          <label className="block text-sm text-slate-300 mb-2" htmlFor="admin-password">
            Password
          </label>
          <input
            id="admin-password"
            type="password"
            value={credentials.password}
            onChange={(event) => setCredentials((current) => ({ ...current, password: event.target.value }))}
            className="mb-6 h-11 w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 text-sm outline-none focus:border-[#00DC82]"
          />
          <button className="h-11 w-full rounded-full bg-[#00DC82] text-[#080C16] font-semibold hover:bg-[#00B86B] transition-colors">
            Sign in
          </button>
        </form>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#080C16] text-white px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <header className="flex flex-col gap-4 border-b border-white/10 pb-5 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00DC82]">
              MockTP Internal
            </p>
            <h1 className="mt-2 text-2xl font-bold tracking-tight sm:text-3xl">Admin Tools</h1>
            <p className="mt-1 text-sm text-slate-400">
              Cleanup tools for beta applications and contact messages.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={loadData}
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-white/10 px-3 text-sm text-slate-200 hover:bg-white/5"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              onClick={logout}
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-white/10 px-3 text-sm text-slate-200 hover:bg-white/5"
            >
              <XCircle size={16} />
              Sign out
            </button>
          </div>
        </header>

        <section className="grid gap-3 py-5 sm:grid-cols-2">
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
            <p className="text-xs text-slate-400">Beta applications</p>
            <p className="mt-1 text-2xl font-semibold">{applications.length}</p>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
            <p className="text-xs text-slate-400">Contact messages</p>
            <p className="mt-1 text-2xl font-semibold">{messages.length}</p>
          </div>
        </section>

        <section className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <div className="flex flex-col gap-3 border-b border-white/10 pb-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold">Beta Applications</h2>
                <p className="mt-1 text-sm text-slate-400">Delete individual applications or clear the full list.</p>
              </div>
              <button
                onClick={clearApplications}
                disabled={busyAction === "clear-beta" || applications.length === 0}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-red-400/30 px-3 text-sm text-red-200 hover:bg-red-500/10 disabled:opacity-50"
              >
                <Trash2 size={15} />
                Clear all
              </button>
            </div>
            <div className="mt-4 space-y-3">
              {loading && <p className="text-sm text-slate-400">Loading applications...</p>}
              {!loading && applications.length === 0 && (
                <p className="rounded-xl border border-white/10 bg-[#080C16] p-4 text-sm text-slate-400">
                  No beta applications to manage.
                </p>
              )}
              {applications.map((application) => (
                <article key={application.id} className="rounded-xl border border-white/10 bg-[#080C16] p-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <h3 className="truncate text-sm font-semibold text-white">
                        {application.name || "Unnamed applicant"}
                      </h3>
                      <p className="truncate text-sm text-slate-400">{application.email}</p>
                      <p className="mt-1 text-xs text-slate-500">
                        Submitted {formatDateTime(application.created_at || application.createdAt)}
                      </p>
                    </div>
                    <button
                      onClick={() => deleteApplication(application)}
                      disabled={busyAction === `beta-${application.id}`}
                      className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-red-400/30 px-3 text-sm text-red-200 hover:bg-red-500/10 disabled:opacity-50"
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <div className="flex flex-col gap-3 border-b border-white/10 pb-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold">Contact Messages</h2>
                <p className="mt-1 text-sm text-slate-400">Delete individual messages or clear the full list.</p>
              </div>
              <button
                onClick={clearContactMessages}
                disabled={busyAction === "clear-contact" || messages.length === 0}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-red-400/30 px-3 text-sm text-red-200 hover:bg-red-500/10 disabled:opacity-50"
              >
                <Trash2 size={15} />
                Clear all
              </button>
            </div>
            <div className="mt-4 space-y-3">
              {loading && <p className="text-sm text-slate-400">Loading contact messages...</p>}
              {!loading && messages.length === 0 && (
                <p className="rounded-xl border border-white/10 bg-[#080C16] p-4 text-sm text-slate-400">
                  No contact messages to manage.
                </p>
              )}
              {messages.map((message) => (
                <article key={message.id} className="rounded-xl border border-white/10 bg-[#080C16] p-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <h3 className="truncate text-sm font-semibold text-white">
                        {message.name || "Unnamed contact"}
                      </h3>
                      <p className="truncate text-sm text-slate-400">{message.email}</p>
                      <p className="mt-1 line-clamp-2 text-sm text-slate-500">{message.message}</p>
                      <p className="mt-1 text-xs text-slate-500">
                        Submitted {formatDateTime(message.created_at)}
                      </p>
                    </div>
                    <button
                      onClick={() => deleteContactMessage(message)}
                      disabled={busyAction === `contact-${message.id}`}
                      className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-red-400/30 px-3 text-sm text-red-200 hover:bg-red-500/10 disabled:opacity-50"
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
